import type { PaginationState } from "@tanstack/react-table";
import { capitalize } from "common/utils/capitalize";
import { useMemo, useState } from "react";
import Button from "ui/buttons/Button";
import Checkbox from "ui/forms/CheckboxOld";
import Input from "ui/forms/Input";
import SimpleSelect from "ui/forms/SimpleSelect";
import type { SelectBasicItem } from "ui/forms/SimpleSelect";
import DataTable from "./DataTable";
import type { DataTableColumn } from "./types";

interface SimpleProduct {
  id: string;
  name: string;
  category: string;
  price: number;
  inStock: boolean;
  sku: string;
  supplier: string;
}

interface OrderItem {
  id: string;
  productName: string;
  quantity: number;
  unitPrice: number;
}

interface Order {
  id: string;
  customerName: string;
  orderDate: string;
  status: string;
  total: number;
  items: OrderItem[];
}

function randomWord(): string {
  const adjectives = ["halcyon", "sleepy", "intrepid", "drunken", "cynical", "bountiful", "colossal", "stealth"];
  const nouns = [
    "ferret",
    "wizard",
    "abacus",
    "lipstick",
    "taylor swift merch",
    "irish wolf hound",
    "Toyota Camry",
    "Marc",
  ];
  return capitalize(
    adjectives[Math.floor(Math.random() * adjectives.length)] + " " + nouns[Math.floor(Math.random() * nouns.length)],
  );
}

function generateSampleProducts(): SimpleProduct[] {
  const category1 = randomWord();
  const category2 = randomWord();
  const suppliers = ["TechCorp", "DisplayCo", "OfficePro", "LightCo", "AudioTech", "StorageCo"];
  const prices = [1299, 29, 129, 399, 299, 599, 49, 39, 45, 89, 199, 25, 149, 79, 19];

  return Array.from({ length: 15 }, (_, i) => ({
    id: String(i + 1),
    name: randomWord(),
    category: i % 3 === 0 ? category1 : category2,
    price: prices[i],
    inStock: Math.random() > 0.2,
    sku: `SKU-${String(i + 1).padStart(3, "0")}`,
    supplier: suppliers[Math.floor(Math.random() * suppliers.length)],
  }));
}

// Sample data for basic examples
const SAMPLE_PRODUCTS: SimpleProduct[] = generateSampleProducts();

function generateSampleOrders(): Order[] {
  const customers = ["Alice Johnson", "Bob Smith", "Carol Williams", "David Brown", "Eva Martinez"];
  const statuses = ["Pending", "Processing", "Shipped", "Delivered"];

  return customers.map((customerName, i) => {
    const itemCount = 2 + Math.floor(Math.random() * 3);
    const items: OrderItem[] = Array.from({ length: itemCount }, (_, j) => ({
      id: `${i + 1}-${j + 1}`,
      productName: randomWord(),
      quantity: 1 + Math.floor(Math.random() * 5),
      unitPrice: 10 + Math.floor(Math.random() * 200),
    }));

    return {
      id: String(i + 1),
      customerName,
      orderDate: new Date(
        2024,
        Math.floor(Math.random() * 12),
        1 + Math.floor(Math.random() * 28),
      ).toLocaleDateString(),
      status: statuses[Math.floor(Math.random() * statuses.length)],
      total: items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0),
      items,
    };
  });
}

const SAMPLE_ORDERS: Order[] = generateSampleOrders();

/**
 * Interactive playground for testing all DataTable props
 */
export function InteractivePlaygroundExample() {
  // Table configuration state
  const [size, setSize] = useState<"xsmall" | "small" | "large">("small");
  const [align, setAlign] = useState<"left" | "center">("left");
  const [cellBorders, setCellBorders] = useState(false);
  const [zebra, setZebra] = useState(false);
  const [rowGaps, setRowGaps] = useState(false);
  const [draggable, setDraggable] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fixedHeader, setFixedHeader] = useState(false);
  const [showColumnHeaders, setShowColumnHeaders] = useState(true);
  const [showHideColumns, setShowHideColumns] = useState(false);
  const [horizontalOverflow, setHorizontalOverflow] = useState(false);
  const [enableColumnResizing, setEnableColumnResizing] = useState(true);
  const [minStartingColumnWidth, setMinStartingColumnWidth] = useState(150);
  const [enableSearch, setEnableSearch] = useState(false);

  // Data state
  const [data, setData] = useState<SimpleProduct[]>(SAMPLE_PRODUCTS);
  const [extraColumns, setExtraColumns] = useState<number>(0);
  const [pagination, setPagination] = useState<PaginationState | undefined>();

  const sizeOptions: SelectBasicItem[] = [
    { type: "item", value: "xsmall", label: "XSmall" },
    { type: "item", value: "small", label: "Small" },
    { type: "item", value: "large", label: "Large" },
  ];

  const alignOptions: SelectBasicItem[] = [
    { type: "item", value: "left", label: "Left" },
    { type: "item", value: "center", label: "Center" },
  ];

  const addRow = (): void => {
    const newId = String(data.length + 1);
    const newProduct: SimpleProduct = {
      id: newId,
      name: randomWord(),
      category: randomWord(),
      price: 0,
      inStock: true,
      sku: `SKU-${newId.padStart(3, "0")}`,
      supplier: randomWord(),
    };
    setData([...data, newProduct]);
  };

  const removeRow = (): void => {
    if (data.length > 1) {
      setData(data.slice(0, -1));
    }
  };

  const addColumn = (): void => {
    setExtraColumns((prev) => prev + 1);
  };

  const removeColumn = (): void => {
    if (extraColumns > 0) {
      setExtraColumns((prev) => prev - 1);
    }
  };

  // Memoize random column headers and cell values to prevent infinite loops
  const extraColumnData = useMemo(() => {
    return Array.from({ length: extraColumns }, (_, i) => ({
      id: `extra-${i}`,
      header: randomWord(),
      cellValue: randomWord(),
    }));
  }, [extraColumns]);

  const editableColumns = useMemo(() => {
    const baseColumns: DataTableColumn<SimpleProduct>[] = [
      {
        field: "name",
        header: "Product Name",
        bold: true,
        editable: true,
      },
      {
        field: "category",
        header: "Category",
        editable: true,
      },
      {
        field: "price",
        header: "Price",
        type: "USD",
        editable: true,
      },
      {
        field: "sku",
        header: "SKU",
        editable: true,
      },
      {
        field: "supplier",
        header: "Supplier",
        editable: true,
      },
      {
        field: "inStock",
        header: "In Stock",
        type: "BOOLEAN",
      },
    ];

    // Add extra columns dynamically
    const dynamicColumns: DataTableColumn<SimpleProduct>[] = extraColumnData.map((col) => ({
      id: col.id,
      header: col.header,
      cell: () => col.cellValue,
    })) as DataTableColumn<SimpleProduct>[];

    return [...baseColumns, ...dynamicColumns];
  }, [extraColumnData]);

  return (
    <div style={{ padding: "20px" }}>
      <h2>Interactive Playground</h2>
      <p style={{ marginBottom: "16px", fontSize: "14px", color: "#666" }}>
        Configure all table properties, add/remove rows and columns, edit data, and see the results in real-time.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "24px" }}>
        {/* Display Options */}
        <div>
          <h4 style={{ marginBottom: "8px" }}>Display Options</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
            <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              Size
              <SimpleSelect.Basic
                items={sizeOptions}
                value={size}
                onValueChange={(value) => setSize(value as typeof size)}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              Alignment
              <SimpleSelect.Basic
                items={alignOptions}
                value={align}
                onValueChange={(value) => setAlign(value as typeof align)}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              Min Column Width
              <Input
                type="number"
                value={minStartingColumnWidth}
                onChange={(e) => setMinStartingColumnWidth(Number(e.currentTarget.value))}
              />
            </label>
          </div>
        </div>

        {/* Styling Options */}
        <div>
          <h4 style={{ marginBottom: "8px" }}>Styling</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Checkbox checked={cellBorders} onChange={(e) => setCellBorders(e.currentTarget.checked)} />
              Cell Borders
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Checkbox checked={zebra} onChange={(e) => setZebra(e.currentTarget.checked)} />
              Zebra Striping
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Checkbox checked={rowGaps} onChange={(e) => setRowGaps(e.currentTarget.checked)} />
              Row Gaps
            </label>
          </div>
        </div>

        {/* Feature Options */}
        <div>
          <h4 style={{ marginBottom: "8px" }}>Features</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Checkbox checked={draggable} onChange={(e) => setDraggable(e.currentTarget.checked)} />
              Draggable Rows
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Checkbox checked={fadeIn} onChange={(e) => setFadeIn(e.currentTarget.checked)} />
              Fade In Animation
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Checkbox checked={loading} onChange={(e) => setLoading(e.currentTarget.checked)} />
              Loading State
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Checkbox checked={fixedHeader} onChange={(e) => setFixedHeader(e.currentTarget.checked)} />
              Fixed Header
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Checkbox checked={showColumnHeaders} onChange={(e) => setShowColumnHeaders(e.currentTarget.checked)} />
              Show Headers
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Checkbox checked={showHideColumns} onChange={(e) => setShowHideColumns(e.currentTarget.checked)} />
              Show/Hide Columns
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Checkbox checked={horizontalOverflow} onChange={(e) => setHorizontalOverflow(e.currentTarget.checked)} />
              Horizontal Overflow
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Checkbox
                checked={enableColumnResizing}
                onChange={(e) => setEnableColumnResizing(e.currentTarget.checked)}
              />
              Column Resizing
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Checkbox checked={enableSearch} onChange={(e) => setEnableSearch(e.currentTarget.checked)} />
              Enable Search
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Checkbox
                checked={!!pagination}
                onChange={(e) => {
                  setPagination(e.currentTarget.checked ? { pageSize: 10, pageIndex: 0 } : undefined);
                }}
              />
              Enable Pagination
            </label>
          </div>
        </div>

        {/* Data Controls */}
        <div>
          <h4 style={{ marginBottom: "8px" }}>Data Controls</h4>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <Button btnSize="medium" btnStyle="secondary" onClick={addRow}>
              Add Row
            </Button>
            <Button btnSize="medium" btnStyle="secondary" onClick={removeRow} disabled={data.length <= 1}>
              Remove Row
            </Button>
            <Button btnSize="medium" btnStyle="secondary" onClick={addColumn}>
              Add Column
            </Button>
            <Button btnSize="medium" btnStyle="secondary" onClick={removeColumn} disabled={extraColumns === 0}>
              Remove Column
            </Button>
            <Button
              btnSize="medium"
              btnStyle="tertiary"
              onClick={() => {
                setData(SAMPLE_PRODUCTS);
                setExtraColumns(0);
              }}
            >
              Reset All
            </Button>
          </div>
        </div>
      </div>

      {/* Table Preview */}
      <div style={fixedHeader ? { maxHeight: "400px", overflow: "auto" } : undefined}>
        <DataTable
          data={loading ? [] : data}
          columns={editableColumns}
          size={size}
          align={align}
          cellBorders={cellBorders}
          zebra={zebra}
          rowGaps={rowGaps}
          draggable={draggable}
          fadeIn={fadeIn}
          loading={loading}
          fixedHeader={fixedHeader}
          showColumnHeaders={showColumnHeaders}
          showHideColumns={showHideColumns}
          horizontalOverflow={horizontalOverflow}
          enableColumnResizing={enableColumnResizing}
          minStartingColumnWidth={minStartingColumnWidth}
          controls={enableSearch ? { search: "Search products..." } : undefined}
          pagination={pagination}
          setPagination={pagination ? setPagination : undefined}
          onEdit={(updatedData, row) => {
            if (row) {
              const rowId = row.original.id;
              setData((prevData) => prevData.map((item) => (item.id === rowId ? { ...item, ...updatedData } : item)));
            }
          }}
        />
      </div>
    </div>
  );
}

/**
 * Nested table example showing orders with expandable line items
 */
export function NestedTableExample() {
  const orderColumns: DataTableColumn<Order>[] = [
    {
      field: "id",
      header: "Order #",
      width: 0.1,
    },
    {
      field: "customerName",
      header: "Customer",
      bold: true,
    },
    {
      field: "orderDate",
      header: "Date",
    },
    {
      field: "status",
      header: "Status",
    },
    {
      field: "total",
      header: "Total",
      type: "USD",
    },
  ];

  const itemColumns: DataTableColumn<OrderItem>[] = [
    {
      field: "productName",
      header: "Product",
      bold: true,
    },
    {
      field: "quantity",
      header: "Qty",
      width: 0.15,
    },
    {
      field: "unitPrice",
      header: "Unit Price",
      type: "USD",
    },
    {
      id: "lineTotal",
      header: "Line Total",
      cell: ({ row }) => `$${(row.original.quantity * row.original.unitPrice).toFixed(2)}`,
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <h2>Nested Table Example</h2>
      <p style={{ marginBottom: "16px", fontSize: "14px", color: "#666" }}>
        Click on a row to expand and see the order line items in a nested table.
      </p>
      <DataTable
        data={SAMPLE_ORDERS}
        columns={orderColumns}
        enableColumnResizing
        subTable={(row) => <DataTable data={row.original.items} columns={itemColumns} size="xsmall" />}
      />
    </div>
  );
}

/**
 * Combined demo component showing all DataTable examples
 */
export default function DataTableExamples() {
  return (
    <div>
      <InteractivePlaygroundExample />
      <hr style={{ margin: "40px 0", border: "none", borderTop: "1px solid #e0e0e0" }} />
      <NestedTableExample />
    </div>
  );
}
